import { useState, useRef } from 'react'
import { View, Text, Platform, TextInput as RNTextInput, type ViewStyle } from 'react-native'
import { Image } from 'expo-image'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeIn, FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Mail, Lock } from '@/components/ui/icons'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { Pressable } from '../../components/ui/Pressable'
import { EmailInput, PasswordInput } from '../../components/ui/StructuredTextInput'
import { BackButton } from '../../components/ui/BackButton'
import { SocialAuthRow } from '../../components/auth/SocialAuthRow'
import { TrustBadges } from '../../components/auth/TrustBadges'
import { isAuthInsurancePartnerTrustBadgeEnabled } from '../../lib/config/insurance-partners'
import { useAuth, authKeys } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { useBiometric } from '../../lib/hooks/use-biometric'
import { useQueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { getContactVerificationRequirement } from '../../lib/utils/account-contact'
import { loginSchema, type LoginInput } from '../../lib/validations/auth.schema'
import { isInvalidCredentialsError, isGoogleEmailExistsError } from '../../lib/errors/service-error'
import { COLORS, FONTS, RADIUS, SPACING } from '../../lib/brand'
import {
  useSemanticColors,
  withAlpha,
  DURATION,
  STAGGER,
  INTERACTION,
  FORM_FIELD_GAP,
} from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'

const isWeb = Platform.OS === 'web'

/**
 * Flexible gap between the three zones of the split layout on a tablet.
 *
 * The split is not desktop-only — an iPad crosses 900 too — and there the
 * canvas is a third taller than any laptop window at the same width: 1376pt
 * of column against ~800pt of form. `AuthScaffold` centres that, which parks
 * the whole screen in a band with ~290pt of dead canvas above and below.
 *
 * These gaps take the surplus instead, so the header sits near the top, the
 * form in the middle and the alternatives near the foot. They claim no height
 * of their own (`flexBasis: 0`), so on a short window — or when the keyboard
 * is up and `KeyboardAvoidingView` has shrunk the canvas — there is nothing to
 * take and the layout is exactly what it was. The cap is the same one the
 * welcome screen uses, so the two screens in one flow drift apart at the same
 * rate; past it the surplus goes back to the scaffold's centring.
 */
const TABLET_BAND_GAP: ViewStyle = {
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 0,
  maxHeight: SPACING['3xl'] * 3,
}

export default function LoginScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, width } = useBreakpoint()
  const {
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInPending,
    signInError,
    googlePending,
    applePending,
    clearAuthErrors,
    profile,
  } = useAuth()
  const queryClient = useQueryClient()
  const { isAvailable, hasBeenPrompted, markPrompted } = useBiometric()
  const sessionExpired = useAuthStore((s) => s.sessionExpired)
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const markCredentialUnlock = useAuthStore((s) => s.markCredentialUnlock)
  const loginAttempts = useAuthStore((s) => s.loginAttempts)
  const lastLoginAttempt = useAuthStore((s) => s.lastLoginAttempt)
  const incrementLoginAttempts = useAuthStore((s) => s.incrementLoginAttempts)
  const resetLoginAttempts = useAuthStore((s) => s.resetLoginAttempts)
  const reducedMotion = useReducedMotion()

  // Breakpoint-driven rhythm. `isWide` maps to our canonical desktop
  // breakpoint (≥1024). `isCompact` is kept for short-height phones so
  // the existing vertical-rhythm math keeps working until the spacing
  // helper retires in Track F.
  const isWide = isDesktop
  // The half of the split layout that is NOT the desktop shell — an iPad, or
  // any tablet-class client past `AuthScaffold`'s 900pt split. `isWide` above
  // deliberately does not cover it: those two branches want opposite things
  // here, tighter type on desktop and more distributed height on a tablet.
  const isTabletSplit = !isDesktop && width >= 900
  const iconColor = colors.textSecondary
  // Hoisted so the subtitle can own the sub→form gap when the badge block
  // is flagged off; otherwise the first input sits 16pt under the subtitle
  // on one build and 32pt under it on another.
  const showTrustBadges = isAuthInsurancePartnerTrustBadgeEnabled()

  const passwordRef = useRef<RNTextInput>(null)

  const [form, setForm] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState<Partial<Record<keyof LoginInput, string>>>({})
  const [rateLimitMinutes, setRateLimitMinutes] = useState(0)

  const sp = useAuthRhythm()

  const updateForm = (key: keyof LoginInput, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const MAX_LOGIN_ATTEMPTS = 5

  const handleLogin = async () => {
    impact(ImpactStyle.Light)
    // Drop any banner left by a previous attempt through another method.
    //
    // First thing in the handler, before the rate-limit and schema branches
    // below, because both of them `return`. With this call further down, a
    // "Contraseña incorrecta" left over from a Google attempt survived a
    // rate-limited or malformed submit and sat next to the new message — two
    // errors on screen, one of them about something the person already stopped
    // doing.
    clearAuthErrors()

    // Client-side rate limiting — exponential backoff after MAX_LOGIN_ATTEMPTS
    if (loginAttempts >= MAX_LOGIN_ATTEMPTS) {
      const backoffMs = Math.min(30_000 * Math.pow(2, loginAttempts - MAX_LOGIN_ATTEMPTS), 300_000)
      const elapsed = Date.now() - lastLoginAttempt
      if (elapsed < backoffMs) {
        const remainingMinutes = Math.ceil((backoffMs - elapsed) / 60_000)
        setRateLimitMinutes(remainingMinutes)
        void notification(NotificationType.Error)
        return
      }
    }
    setRateLimitMinutes(0)

    const result = loginSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof LoginInput, string>> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof LoginInput
        fieldErrors[key] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }
    setErrors({})

    // Block AuthGate redirect BEFORE signIn so the SIGNED_IN event
    // doesn't trigger a redirect while we check biometric availability
    setCompletingSignup(true)

    try {
      await signIn(result.data)
    } catch (err) {
      if (__DEV__) console.error('[Login] login failed:', err)
      if (isInvalidCredentialsError(err)) {
        incrementLoginAttempts()
      } else {
        resetLoginAttempts()
      }
      void notification(NotificationType.Error)
      setCompletingSignup(false)
      return
    }

    // Clear password from memory immediately after successful auth
    setForm((prev) => ({ ...prev, password: '' }))
    markCredentialUnlock()
    resetLoginAttempts()
    void notification(NotificationType.Success)

    // Defer Face ID enrollment — never interrupt first successful login. The
    // probe is also OFF the post-auth critical path: awaiting three OS calls
    // here keeps isCompletingSignup held while the routing gate waits, and a
    // slow/unsettled prompt widens the window where a late session read can
    // misroute. Fire-and-forget; the only cost of an error is telemetry.
    void (async () => {
      try {
        const available = await isAvailable()
        const prompted = await hasBeenPrompted()
        if (available && !prompted) await markPrompted()
      } catch (err) {
        captureError(err, { screen: 'login', action: 'biometric-check' })
      }
    })()

    // Hold isCompletingSignup through replace so tabs cannot Redirect to
    // welcome on a one-frame isAuthenticated lag after credential auth.
    const route = await resolvePostAuthRoute().catch(() => '/(tabs)/explore')
    router.replace(route as never)
  }

  const handleSocialSignIn = async (
    provider: () => Promise<unknown>,
    providerName: 'google' | 'apple',
  ) => {
    // The password banner must not survive into a social attempt.
    clearAuthErrors()
    setCompletingSignup(true)
    try {
      await provider()
      if (Platform.OS === 'web') return

      const { finishSocialSignIn } = await import('../../lib/auth/finish-social-sign-in')
      const result = await finishSocialSignIn({
        router,
        screen: 'login',
        providerName,
        allowCancel: true,
      })
      if (result !== 'ok') {
        setCompletingSignup(false)
        return
      }

      // Same non-critical-path rule as the credential flow: a slow OS probe
      // here must not hold the social handoff in a pending state.
      void (async () => {
        try {
          const available = await isAvailable()
          const prompted = await hasBeenPrompted()
          if (available && !prompted) await markPrompted()
        } catch (err) {
          captureError(err, { screen: 'login', action: 'social-biometric-check' })
        }
      })()
    } catch (err) {
      if (__DEV__) console.error('[Login] social sign-in failed:', err)
      // Ese correo ya es de una cuenta con otro método. Era la única de las tres
      // superficies sociales sin esta rama, así que acá salía el código crudo:
      // "No se pudo iniciar sesión con Google. (EMAIL ALREADY REGISTERED)".
      // Inglés de máquina dentro de una frase en español, y sin decirle a la
      // persona qué hacer. Ya está en la pantalla correcta: solo falta el
      // mensaje que le dice con qué entrar.
      if (isGoogleEmailExistsError(err)) {
        setCompletingSignup(false)
        toast.info(t('auth.errors.google_email_exists'))
        captureError(err, { screen: 'login', action: 'social-sign-in' })
        return
      }
      void notification(NotificationType.Error)
      // El código de la falla va a Sentry, no a la cara de la persona. Un
      // "(EMAIL ALREADY REGISTERED)" pegado a un mensaje en español no le dice
      // nada a quien lo lee y hace ver rota la app.
      toast.error(
        errorMessage(
          err,
          t(providerName === 'google' ? 'auth.errors.google_failed' : 'auth.errors.social_failed'),
        ),
      )
      captureError(err, { screen: 'login', action: 'social-sign-in' })
      setCompletingSignup(false)
    }
  }

  const handleGoogle = () => handleSocialSignIn(signInWithGoogle, 'google')
  const handleApple = () => handleSocialSignIn(signInWithApple, 'apple')

  const handlePhone = () => {
    clearAuthErrors()
    router.push({ pathname: '/(auth)/phone-login', params: { mode: 'login' } })
  }

  const resolvePostAuthRoute = async (): Promise<string> => {
    // Single-read, null-safe. The CACHED session (seeded by the
    // SIGNED_IN/TOKEN_REFRESHED listener or seedSessionCache) is the only
    // source trusted here: a raw getSession() right after signIn races the
    // native SecureStore write and can answer null for an instant. Interpreting
    // that null as "no verified contact" was the visible half of the double
    // login — the user was bounced to verify-phone, came back, and had to log
    // in again once the session was finally hot. A null user must NEVER resolve
    // to a contact step: default to Explore, and the root AuthGate recomputes
    // any real contact requirement from the live session + profile.
    const cached = queryClient.getQueryData<Session | null>(authKeys.session())
    if (cached?.user) {
      const requirement = getContactVerificationRequirement(cached.user, profile ?? null)
      if (requirement === 'email') return '/(auth)/verify-email'
      if (requirement === 'phone') return '/(auth)/verify-phone'
    }
    return '/(tabs)/explore'
  }

  const webCursor = isWeb ? { cursor: 'pointer' as const } : {}

  // ============================
  //  SHARED FORM CONTENT
  // ============================
  const formContent = (
    <>
      {/* ─── ZONE A: Navigation ─── */}
      <BackButton />

      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal)}
        style={{
          // Phone/native is never `isDesktop`. Desktop form column keeps the
          // editorial left lockup beside BrandPanel; phone centres the mark
          // with the headline and trust row as one composition.
          alignSelf: isWide ? 'flex-start' : 'center',
          marginTop: SPACING.md,
          marginBottom: SPACING.sm,
        }}
      >
        <Image
          source={require('../../assets/images/logo-horizontal.png')}
          style={{ width: 100, height: 32 }}
          contentFit="contain"
          accessibilityLabel={t('common.app_name')}
        />
      </Animated.View>

      {/* ─── ZONE B: Header ─── */}
      <Animated.Text
        entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
        accessibilityRole="header"
        minimumFontScale={0.82}
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: isWide ? 28 : 32,
          lineHeight: isWide ? 34 : 38,
          marginTop: sp.headerTop,
          color: colors.textPrimary,
          width: '100%',
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {t('login.headline')}
      </Animated.Text>

      <Animated.Text
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
        }
        style={{
          fontFamily: FONTS.body,
          fontSize: 16,
          lineHeight: 24,
          marginTop: sp.headlineToSub,
          marginBottom: showTrustBadges ? SPACING.md : sp.subToForm,
          // Was textPrimary — the subtitle rendered as dark as the 32pt
          // headline directly above it, so the header had no hierarchy.
          color: colors.textSecondary,
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {t('login.subtitle')}
      </Animated.Text>

      {showTrustBadges ? (
        <Animated.View
          collapsable={false}
          entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal).delay(STAGGER * 3)}
          style={{
            marginBottom: sp.subToForm,
            alignSelf: isWide ? 'flex-start' : 'center',
          }}
        >
          <TrustBadges isDark={isDark} align={isWide ? 'start' : 'center'} />
        </Animated.View>
      ) : null}

      {/* Zone B → Zone C. Header above, the thing you came to fill in below. */}
      {isTabletSplit ? <View style={TABLET_BAND_GAP} /> : null}

      {/* ─── ZONE C: Form ─── */}

      {/* Session expired banner */}
      {sessionExpired && (
        <Animated.View
          collapsable={false}
          entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal)}
          style={{
            borderRadius: RADIUS.md,
            backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.2 : 0.08),
            paddingHorizontal: SPACING.md,
            paddingVertical: SPACING.sm + SPACING.xs,
            marginBottom: SPACING.lg - SPACING.xs,
          }}
          accessibilityRole="alert"
        >
          <Text
            style={{
              textAlign: 'center',
              fontSize: 14,
              color: colors.textAccent,
              fontFamily: FONTS.body,
            }}
          >
            {t('auth.session_expired')}
          </Text>
        </Animated.View>
      )}

      {/* Email */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
        }
      >
        <EmailInput
          label={t('auth.email')}
          placeholder={t('auth.email_placeholder')}
          value={form.email}
          onChangeText={(v) => updateForm('email', v)}
          returnKeyType="next"
          blurOnSubmit={false}
          onSubmitEditing={() => passwordRef.current?.focus()}
          error={errors.email}
          leftIcon={<Mail size={18} color={iconColor} />}
          testID="login.email-input"
        />
      </Animated.View>

      {/* Password */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
        }
      >
        <PasswordInput
          ref={passwordRef}
          label={t('auth.password')}
          placeholder={t('auth.password_placeholder')}
          value={form.password}
          onChangeText={(v) => updateForm('password', v)}
          autoComplete="current-password"
          textContentType="password"
          returnKeyType="go"
          onSubmitEditing={handleLogin}
          error={errors.password}
          leftIcon={<Lock size={18} color={iconColor} />}
          testID="login.password-input"
        />
      </Animated.View>

      {/* Forgot password — contextually tied to password field */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 5)
        }
        style={{
          // The password field ships FORM_FIELD_GAP (32) underneath it — the
          // gap that separates one FIELD from the next. Stacked on top of the
          // 44pt touch box's own centring, the link ended up floating in the
          // middle of a 104pt band between the field and the CTA instead of
          // reading as part of the password row. Pull it back so the link sits
          // one small step (8pt) under its own field; the group gap to the
          // action zone is still owned by `sp.formToAction` below.
          marginTop: SPACING.sm - FORM_FIELD_GAP,
        }}
      >
        {/* react-native-web drops hitSlop, so the 13pt line of text was a
            ~24pt tall target. The box has to be the target: minHeight plus
            centred content. */}
        <Pressable
          onPress={() => router.push('/(auth)/forgot-password')}
          style={({ pressed }) => ({
            opacity: pressed ? 0.6 : 1,
            alignSelf: 'flex-end',
            minHeight: INTERACTION.minTouchTarget,
            justifyContent: 'center',
            ...webCursor,
          })}
          hitSlop={8}
          testID="login.forgot-password-link"
          accessibilityRole="link"
          accessibilityLabel={t('auth.forgot_password')}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textAccent,
            }}
          >
            {t('auth.forgot_password')}
          </Text>
        </Pressable>
      </Animated.View>

      {/* ─── ZONE D: Action ─── */}
      <View style={{ marginTop: sp.formToAction }}>
        {/* Error banner — auth error or rate limit */}
        <AuthErrorBanner
          error={
            rateLimitMinutes > 0
              ? t('auth.errors.too_many_attempts', { minutes: rateLimitMinutes })
              : signInError
                ? errorMessage(signInError, t('auth.errors.invalid_credentials'))
                : null
          }
        />

        {/* CTA */}
        <AuthGradientButton
          title={t('auth.sign_in_button')}
          onPress={handleLogin}
          loading={signInPending}
          testID="login.submit-button"
          entering={
            reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 6)
          }
        />
      </View>

      {/* ─── ZONE E: Alternatives ─── */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal).delay(STAGGER * 7)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          // One token above and below, same as welcome and register. At
          // 32 over 20 the connector rode high in its own gap and no two
          // screens in the flow spaced this hairline the same way.
          marginTop: SPACING.lg,
          marginBottom: SPACING.lg,
        }}
      >
        <View
          style={{
            flex: 1,
            height: 1,
            backgroundColor: colors.border,
          }}
        />
        {/* Same connector as welcome and register: sentence case, 13pt,
            secondary. `auth.or_divider` is the bare letter "o" — one
            lowercase character between two hairlines reads as a bullet, not
            as language — and the all-caps + 1pt tracking made this screen's
            divider a different component from the one on the two screens
            either side of it. */}
        <Text
          style={{
            marginHorizontal: SPACING.md,
            fontFamily: FONTS.body,
            fontSize: 13,
            letterSpacing: 0.2,
            color: colors.textSecondary,
          }}
        >
          {t('welcome.divider')}
        </Text>
        <View
          style={{
            flex: 1,
            height: 1,
            backgroundColor: colors.border,
          }}
        />
      </Animated.View>

      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 8)
        }
      >
        <SocialAuthRow
          onGoogle={handleGoogle}
          onApple={handleApple}
          onPhone={handlePhone}
          googleLoading={googlePending}
          appleLoading={applePending}
          isDark={isDark}
          testIDPrefix="login"
        />
      </Animated.View>

      {/* Zone E → Zone F. Drops the "¿No tenés una cuenta?" line to the foot of
          the canvas instead of leaving it trailing the social row. */}
      {isTabletSplit ? <View style={TABLET_BAND_GAP} /> : null}

      {/* ─── ZONE F: Footer ─── */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 9)}
        style={{
          flexDirection: 'row',
          // A non-wrapping row does not clip at one edge — it overflows both.
          // At 2x system text this sentence plus the link is wider than the
          // screen, and the glyphs spill past their own view bounds (Android
          // does not clip a Text to its box), so the render lost the leading
          // "¿" on the left and the last letter of the link on the right.
          // Same two properties `AuthFooterLink` and the phone-login footer
          // already carry; at scale 1 the row still fits on one line, so
          // nothing about the approved screen moves.
          flexWrap: 'wrap',
          rowGap: SPACING.xs,
          justifyContent: isWide ? 'flex-start' : 'center',
          alignItems: 'center',
          marginTop: sp.altToFooter,
          // The link below is now a 44pt box with centred text, so it carries
          // its own bottom air; the extra padding double-counted it.
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            flexShrink: 1,
            color: colors.textSecondary,
          }}
        >
          {`${t('auth.dont_have_account')} `}
        </Text>
        <Pressable
          onPress={() => router.replace('/(auth)/register')}
          hitSlop={8}
          style={{
            minHeight: INTERACTION.minTouchTarget,
            justifyContent: 'center',
            // Same inset the phone-login footer uses, so the word gap before
            // the link is identical on both halves of the same footer line.
            paddingHorizontal: SPACING.xs,
            ...webCursor,
          }}
          testID="login.register-link"
          accessibilityRole="link"
          accessibilityLabel={`${t('auth.dont_have_account')} ${t('auth.register')}`}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textAccent,
            }}
          >
            {t('auth.register')}
          </Text>
        </Pressable>
      </Animated.View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
    >
      {formContent}
    </AuthScaffold>
  )
}
